import React, { ReactNode } from 'react';
import { DeviceSearchRow, OpenAlert } from '../../types';
import DecommissionedBadge from '../DecommissionedBadge';
import ModelLink from '../ModelLink';
import {
  airflowCalibrationLabel,
  filterIdentitySourceLabel,
  missingFieldLabel,
  missingFieldsLabel,
  modelSourceLabel,
  motorTypeLabel,
  qualityLabel,
  ratedStaticLabel,
  sourceLabel,
} from '../../utils/equipmentQuality';
import { componentName, sideName, tonnageMarkerTitle } from '../../utils/tonnageWords';
import { CsvValue } from '../../utils/chartCsv';
import { returnGrilleText, searchRowClipLabel, WHOLE_HOUSE_FILTER } from '../../utils/clipLabel';

/**
 * Plan 101 — Device List (`/edit-device`) column registry: the single source
 * of truth for every column the table can show. Header row, body cells, the
 * filter checklist, and the column chooser all iterate this array; adding a
 * column is one entry here + one backend allowlist entry
 * (device-data-api deviceSearchQuery.ts).
 *
 * Registry array order = default display order. The default-visible set and
 * order reproduce the pre-101 Device List table (ID, Name, User, Address,
 * Zip, HVAC, Filter, Cal. Offset, Fanside, Entity Path — Source and Actions
 * are page-level trailing columns, not registry entries); everything else is
 * opt-in via the chooser. The user's saved columnOrder (deviceListPrefs)
 * overrides it; a registry key absent from saved prefs is a newly added
 * column and is spliced in at its registry position, shown or hidden per its
 * `defaultVisible` (never conflated with user-hidden columns — those live in
 * a separate `hidden` list).
 */

export type ColumnType = 'text' | 'boolean' | 'enum' | 'number' | 'date' | 'prefix';
export type ColumnGroup =
  | 'Identity'
  | 'Lifecycle'
  | 'Location'
  | 'Configuration'
  | 'Filter'
  | 'App'
  | 'Operations'
  | 'Telemetry';

export interface ColumnDef {
  /** Stable id; also the sort/filter key sent to the API unless overridden. */
  key: string;
  label: string;
  group: ColumnGroup;
  type: ColumnType;
  defaultVisible: boolean;
  sortable: boolean;
  /** Composite columns (Device, Owner) sort by ONE declared atomic field. */
  sortKey?: string;
  filterable: boolean;
  /** Filter param key when it differs from `key` (e.g. User filters by uid). */
  filterKey?: string;
  /** Facet name in the search response that supplies enum/prefix options. */
  facet?: string;
  defaultSortDir: 'asc' | 'desc';
  /** px, drives the <colgroup>; user resize overrides. */
  defaultWidth: number;
  /** Resize clamp floor (default 60). */
  minWidth?: number;
  render: (row: DeviceSearchRow) => ReactNode;
  /** Header tooltip (sort hints, staleness notes). */
  tooltip?: string;
  /** Display label for a facet option value (enum filter editors). */
  optionLabel?: (value: string) => string;
  /**
   * The filter's list items are integer ids (the server's `intValues`): a
   * saved report may hold them only as the facet lists them, plain decimal
   * with no leading zero, so `canonicalFilterValue` spells them that way.
   */
  intValues?: boolean;
  /**
   * Filter-only pseudo-column: offered in the filter menu but never rendered
   * as a table column and never listed in the column chooser. `not_tags` uses
   * this — "everything NOT tagged X" is a way to filter, not something a row
   * can display.
   */
  filterOnly?: boolean;
  /**
   * FS-745 — the cell as a plain value for the CSV download. Omit it when the
   * row field named by `key` already is that value; `csvValueOf` then reads
   * it, printing flags as Yes/No and lists joined with '; '.
   */
  csv?: (row: DeviceSearchRow) => CsvValue;
  /**
   * FS-745 — the CSV header when the label leaves the file's unit unsaid (a
   * cell that prints "72%" on screen is the bare number 72 in the file).
   * Every header must stay unique across the display columns; a test holds it.
   */
  csvHeader?: string;
}

const dash = <span className="text-muted-foreground">—</span>;

const text = (v: string | null | undefined): ReactNode => (v ? v : dash);
const num = (v: number | null | undefined, digits = 0): ReactNode =>
  v === null || v === undefined ? dash : v.toFixed(digits);
const yesNo = (v: boolean | null | undefined): ReactNode =>
  v === null || v === undefined ? dash : v ? 'Yes' : 'No';
const date = (v: string | null | undefined): ReactNode =>
  v ? <span className="whitespace-nowrap tabular-nums">{v}</span> : dash;
const mono = (v: string | null | undefined): ReactNode =>
  v ? <span className="font-mono text-xs break-all">{v}</span> : dash;

/**
 * FS-838 — the Tonnage cell: the number, and a red ⚠ when another credible
 * value disagrees. The marker's title says both values and why the one shown
 * is used, in `tonnageWords`' sentences, so the Overview and this cell cannot
 * word one disagreement two ways. A ductless head the resolver would not size
 * has no number and still carries the marker.
 */
export const tonnageCell = (d: DeviceSearchRow): ReactNode => {
  const value = tonnageNumberText(d) ?? dash;
  if (d.app_hvac_tonnage_conflict !== true) return value;
  return (
    <>
      {value}{' '}
      <span
        data-testid="tonnage-conflict-marker"
        aria-label="Tonnage in conflict"
        title={tonnageMarkerTitle(d) ?? undefined}
        className="text-status-error-foreground"
      >
        ⚠
      </span>
    </>
  );
};

/**
 * FS-838 review F3 — the stored tonnage exactly as stored: `3.25` stays
 * `3.25` (a rounded cell would differ from the CSV and the sort, which read
 * the number itself), trailing zeros trimmed (`3`, not `3.0`). A blower
 * unit's "up to N tons" last resort reads `up to 4`, so a maximum never
 * looks like a firm size. Null for no value.
 */
export const tonnageNumberText = (d: DeviceSearchRow): string | null => {
  const v = d.app_hvac_tonnage;
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return null;
  const shown = String(Number(v));
  return d.app_hvac_tonnage_capacity_source === 'blower_maximum' ? `up to ${shown}` : shown;
};

/** `3.5 t (outdoor unit, manufacturer document)`, or null when nothing disagrees. */
export const disagreeingTonnageText = (d: DeviceSearchRow): string | null => {
  const alt = d.app_hvac_tonnage_alt;
  if (alt === null || alt === undefined) return null;
  const source = d.app_hvac_tonnage_alt_source
    ? sourceLabel(d.app_hvac_tonnage_alt_source)
    : 'nameplate, origin not recorded';
  return `${alt} t (${sideName(d.app_hvac_tonnage_alt_side)}, ${source})`;
};

/**
 * FS-593 — where this Clip's filter sits, in words: "Return grille 1 of 2
 * (Hallway)", "Whole-house filter", "Not recorded" (a filter record with no
 * type), or null for a dash (no filter, or a row the server has not
 * backfilled — which includes every row from an API before FS-593). FS-825:
 * the grille and whole-house words are utils/clipLabel's, so this column and
 * the Clip label never word one filter two ways.
 *
 * The grille count is the server's `app_hvac_grille_count`, null when two
 * records name one Clip; the "of N" is then left off rather than guessed.
 * A null type reads as a record with no type only when the row shows the
 * slot list was projected (a grille count) or carries a name — otherwise a
 * row that simply has not been re-synced would read as a gap in the app.
 */
export const filterLocationText = (d: DeviceSearchRow): string | null => {
  const type = d.app_filter_type;
  const name = d.app_filter_location ?? null;
  if (type === undefined) return null;
  if (type === 'furnace') return name ? `${WHOLE_HOUSE_FILTER} (${name})` : WHOLE_HOUSE_FILTER;
  if (type === 'airReturns') {
    const base = returnGrilleText(d.app_filter_slot_ordinal ?? null, d.app_hvac_grille_count ?? null);
    return name ? `${base} (${name})` : base;
  }
  if (type === null) {
    const projected = d.app_hvac_grille_count != null && Boolean(d.app_filter_summary);
    if (name) return `Not recorded (${name})`;
    return projected ? 'Not recorded' : null;
  }
  // A type this bundle has no words for is shown as stored, not hidden.
  return name ? `${type} (${name})` : type;
};

const FILTER_TYPE_WORDS: Record<string, string> = {
  airReturns: 'Return grille',
  furnace: 'Whole-house (furnace)',
};
export const filterTypeLabel = (value: string): string => FILTER_TYPE_WORDS[value] ?? value;

/** Compact relative time for Last Heard ("21 min ago"); sorts stay exact. */
export const relTime = (iso: string, now: number = Date.now()): string => {
  const min = Math.floor((now - Date.parse(iso)) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
};

// Plan 103 — open-alert chips. Short review labels for the known alert
// types; anything new falls back to a humanized type slug.
const ALERT_LABELS: Record<string, string> = {
  humidity_out_of_range: 'Humidity',
  temperature_extreme: 'Temperature',
  no_connection: 'No connection',
  fan_usage_high: 'Fan usage',
  filter_needs_replacement: 'Filter',
  device_installed_backwards: 'Backwards',
  continuous_fan_mode: 'Continuous fan',
  battery_needs_replacement: 'Battery',
  first_filter_readings_received: 'First data',
};
export const alertLabel = (type: string): string =>
  ALERT_LABELS[type] ?? type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const SEVERITY_CHIP: Record<string, string> = {
  urgent: 'bg-status-error border-status-error-border text-status-error-foreground',
  warning: 'bg-status-warning border-status-warning-border text-status-warning-foreground',
  info: 'bg-status-info border-status-info-border text-status-info-foreground',
};

const alertChips = (alerts: OpenAlert[] | null | undefined): ReactNode =>
  !alerts || alerts.length === 0 ? (
    dash
  ) : (
    <span className="flex flex-wrap gap-1">
      {alerts.map((a) => (
        <span
          key={a.type}
          className={`inline-block px-1.5 py-0.5 rounded-sm border text-xs whitespace-nowrap ${
            SEVERITY_CHIP[a.severity] ?? 'bg-status-neutral border-status-neutral-border text-status-neutral-foreground'
          } ${a.internal_only ? 'border-dashed' : ''}`}
        >
          {alertLabel(a.type)}
        </span>
      ))}
    </span>
  );

/** FS-501 — tag names as neutral chips; deliberately unlike alert chips, which
 *  carry severity colour and must stay the only coloured chips in the row. */
const tagChips = (tags: string[] | null | undefined): ReactNode =>
  !tags || tags.length === 0 ? (
    dash
  ) : (
    <span className="flex flex-wrap gap-1">
      {tags.map((t) => (
        <span
          key={t}
          className="inline-block px-1.5 py-0.5 rounded-sm border bg-slate-50 border-border text-slate-700 text-xs whitespace-nowrap"
        >
          {t}
        </span>
      ))}
    </span>
  );

const ratedStaticText = (d: DeviceSearchRow): string =>
  ratedStaticLabel({
    pa: d.app_hvac_rated_esp_pa,
    original: d.app_hvac_rated_esp_original,
    kind: d.app_hvac_rated_esp_kind,
    catalogModelId: d.app_hvac_catalog_model_id,
  });

/**
 * FS-752 — the Missing info cell: each key the server listed, with the reason
 * its own quality column carries. One function for the cell and the CSV, so
 * the file says exactly what the screen does.
 */
export const missingInfoText = (d: DeviceSearchRow): string =>
  missingFieldsLabel(d.app_missing_fields, {
    tonnage: d.app_hvac_tonnage_quality,
    indoor_record: d.app_indoor_record_quality,
    motor_type: d.app_hvac_blower_type_quality,
    filter_record: d.app_filter_quality,
  });

export const COLUMN_REGISTRY: ColumnDef[] = [
  // --- Default-visible set: the pre-101 Device List columns, in order --------
  {
    key: 'id',
    label: 'ID',
    group: 'Identity',
    type: 'number',
    defaultVisible: true,
    sortable: true,
    filterable: false, // no server filter (int4-guarded free-text `q` matches ids)
    defaultSortDir: 'asc',
    defaultWidth: 85,
    render: (d) => d.id,
  },
  {
    key: 'name',
    label: 'Name',
    group: 'Identity',
    type: 'text',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 210,
    minWidth: 120,
    render: (d) => (
      <span className="font-mono text-xs break-all">
        <span className={d.decommissioned ? 'line-through text-muted-foreground' : ''}>{d.name}</span>
        <DecommissionedBadge decommissioned={d.decommissioned} className="ml-1 normal-case!" />
      </span>
    ),
  },
  {
    key: 'app_owner_name',
    label: 'User',
    group: 'App',
    type: 'enum',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    filterKey: 'app_owner_uid',
    facet: 'app_owner',
    defaultSortDir: 'asc',
    defaultWidth: 150,
    render: (d) => text(d.app_owner_name),
    tooltip: 'MyFluxHive app owner from the registration projection',
  },
  {
    key: 'street_address',
    label: 'Address',
    group: 'Location',
    type: 'text',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 180,
    render: (d) => text(d.street_address),
  },
  {
    key: 'postal_code',
    label: 'Zip',
    group: 'Location',
    type: 'text',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 95,
    render: (d) => text(d.postal_code),
  },
  {
    key: 'app_hvac_name',
    label: 'HVAC',
    group: 'App',
    type: 'enum',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    filterKey: 'app_hvac_id',
    facet: 'app_hvac',
    defaultSortDir: 'asc',
    defaultWidth: 130,
    render: (d) => text(d.app_hvac_name),
  },
  {
    key: 'app_hvac_manufacturer',
    label: 'HVAC Brand',
    group: 'App',
    type: 'enum',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 145,
    render: (d) => text(d.app_hvac_manufacturer),
    tooltip:
      'Equipment manufacturer read from the nameplate photo or decoded ' +
      'from the model number',
  },
  {
    key: 'app_hvac_tonnage',
    label: 'Tonnage',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 90,
    // FS-838: the column sorts and filters on the number it shows; the
    // marker only flags a disagreement and is not part of the order.
    render: (d) => tonnageCell(d),
    tooltip:
      'The system tonnage: the cooling size taken from the furnace or air handler first, ' +
      'then the indoor coil, then the outdoor unit. ⚠ marks a system where another ' +
      'credible value disagrees; hover it for that value.',
  },
  {
    key: 'app_hvac_btu_rating',
    label: 'BTU',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 95,
    render: (d) => num(d.app_hvac_btu_rating),
    tooltip:
      'Furnace or air-handler heating capacity in BTU/h, read from the nameplate or ' +
      'decoded from the model number. Heating BTU is never converted to tons.',
  },
  // Plan 119 Phase 2 (FS-460): tonnage quality sits next to the tonnage, so an
  // operator scanning the list can see at a glance which numbers are worth
  // acting on. The code is computed on the server now, which is what makes
  // the column sortable — the client-side version had nothing to order by,
  // and the header carried no sort control at all.
  //
  // The sort is on the RANK (1 = best), so the order is the ladder rather
  // than the alphabet. The filter is on the code itself.
  {
    key: 'app_hvac_tonnage_quality',
    label: 'Tonnage Quality',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    sortKey: 'app_hvac_tonnage_quality_rank',
    filterable: true,
    facet: 'app_hvac_tonnage_quality',
    defaultSortDir: 'asc',
    defaultWidth: 200,
    render: (d) => qualityLabel(d.app_hvac_tonnage_quality),
    csv: (d) => qualityLabel(d.app_hvac_tonnage_quality),
    optionLabel: (value) => qualityLabel(value),
    tooltip:
      'How the system tonnage was established, best first: the indoor unit\'s manufacturer ' +
      'document, a person\'s entry, label photos and model numbers on the indoor unit, then ' +
      'the same for the indoor coil, then for the outdoor unit. A disagreement does not ' +
      'change the rung; see Tonnage In Conflict.',
  },
  {
    key: 'app_hvac_blower_type_quality',
    label: 'Motor Type Quality',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    sortKey: 'app_hvac_blower_type_quality_rank',
    filterable: true,
    facet: 'app_hvac_blower_type_quality',
    defaultSortDir: 'asc',
    defaultWidth: 200,
    render: (d) => qualityLabel(d.app_hvac_blower_type_quality),
    csv: (d) => qualityLabel(d.app_hvac_blower_type_quality),
    optionLabel: (value) => qualityLabel(value),
    tooltip:
      'How the blower motor family was established, best first. A family ' +
      'recorded as unknown means the reading could not tell, which is not ' +
      'the same as nobody having looked',
  },
  // FS-752: the furnace or air handler RECORD, not its photo — a model number
  // the catalog resolved to an indoor unit with a manual behind it. Sorted on
  // the rank like the two columns beside it, so the order is the ladder.
  {
    key: 'app_indoor_record_quality',
    label: 'Furnace or air handler record',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    sortKey: 'app_indoor_record_quality_rank',
    filterable: true,
    facet: 'app_indoor_record_quality',
    defaultSortDir: 'asc',
    defaultWidth: 230,
    render: (d) => qualityLabel(d.app_indoor_record_quality),
    csv: (d) => qualityLabel(d.app_indoor_record_quality),
    optionLabel: (value) => qualityLabel(value),
    tooltip:
      'How complete the furnace or air handler record is, best first. Complete ' +
      'means the model number matched our catalog as a furnace or air handler ' +
      'and a manual for it is on file. A photo alone does not count',
  },
  {
    key: 'app_filter_quality',
    label: 'Filter Curve Quality',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    sortKey: 'app_filter_quality_rank',
    filterable: true,
    facet: 'app_filter_quality',
    defaultSortDir: 'asc',
    defaultWidth: 200,
    render: (d) => qualityLabel(d.app_filter_quality),
    csv: (d) => qualityLabel(d.app_filter_quality),
    optionLabel: (value) => qualityLabel(value),
    tooltip:
      'How good the pressure curve behind this filter is. A certified curve ' +
      'whose size matches the filter recorded in the app is the only one ' +
      'good enough to calibrate an airflow number against',
  },
  // FS-593 — where this Clip's filter sits on its system and how many return
  // grilles the app recorded. Opt-in like the rest of the group. Server-side
  // scalars off the Clip's own projection row; the slot list itself (which
  // names other Clips) never reaches the list.
  {
    key: 'app_filter_location',
    label: 'Filter location',
    group: 'App',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 210,
    csv: filterLocationText,
    render: (d) => {
      const text = filterLocationText(d);
      if (text === null) return dash;
      return (
        <span
          title={
            text.startsWith('Not recorded')
              ? 'The app has a filter record for this Clip but no filter type (return grille or furnace) on it.'
              : "Where this Clip's filter sits on its HVAC system, as the app recorded it. Sorts and filters on the homeowner's name for it."
          }
        >
          {text}
        </span>
      );
    },
    tooltip:
      "Where this Clip's filter sits: which return grille (of how many), or the " +
      "whole-house filter at the furnace. Sorts and filters on the homeowner's name for the spot",
  },
  {
    key: 'app_filter_type',
    label: 'Filter type',
    group: 'App',
    type: 'enum',
    // Filter-only: Filter location already shows the type in words; this is
    // how an operator narrows the list to grilles or to whole-house filters.
    filterOnly: true,
    defaultVisible: false,
    sortable: false,
    filterable: true,
    facet: 'app_filter_type',
    defaultSortDir: 'asc',
    defaultWidth: 160,
    render: () => null,
    optionLabel: (value) => filterTypeLabel(value),
    tooltip: "Show only Clips whose filter is a return grille, or the whole-house filter",
  },
  {
    key: 'app_hvac_grille_count',
    label: 'Recorded grilles',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    // Display only: the list API's contract sorts and filters on the filter
    // type and location, not on this count.
    sortable: false,
    filterable: false,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) =>
      d.app_hvac_grille_count === null || d.app_hvac_grille_count === undefined ? (
        dash
      ) : (
        <span className="tabular-nums">{d.app_hvac_grille_count}</span>
      ),
    tooltip:
      'How many return grilles the app recorded on this HVAC system. Blank when ' +
      'not synced yet, or when two filter records name the same Clip',
  },
  // FS-541, Randy 2026-08-24: one set on the Device List, sortable and
  // filterable — not a separate tab. FS-752 narrowed it to information, not
  // photos: the cooling capacity, the furnace or air handler record, the
  // blower motor type and the filter. Which keys exist comes from the server
  // facet; the client adds none of its own.
  {
    key: 'app_missing_fields',
    label: 'Missing info',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    sortKey: 'app_missing_count',
    filterable: true,
    facet: 'app_missing_fields',
    defaultSortDir: 'desc',
    defaultWidth: 320,
    render: (d) => missingInfoText(d),
    csv: (d) => missingInfoText(d),
    optionLabel: (value) => missingFieldLabel(value),
    tooltip:
      'What is missing or unconfirmed about the outdoor unit, the furnace or ' +
      'air handler, and the filter, each with the reason. Photos are not ' +
      'listed. Sorts by how many things are missing',
  },
  // Plan 123 Phase 1 (FS-541), Chris 2026-09-18: one point for every part of
  // the system we still need information about — nothing needed is a 0, and
  // the operator sorts by it to find the homes worth writing to. It counts
  // PARTS, so it can be a smaller number than Missing info beside it, which
  // counts facts.
  {
    key: 'app_equipment_score',
    label: 'Equipment score',
    group: 'App',
    type: 'number',
    // The one column in this group the operator gets without asking: it is
    // the whole point of the set, and a 0 on every row is good news.
    defaultVisible: true,
    sortable: true,
    filterable: true,
    // Worst first — the reason to sort by this is to find the gaps.
    defaultSortDir: 'desc',
    defaultWidth: 175,
    // 0 is a real answer and prints as 0. A row with no score at all (an API
    // that predates the column) prints nothing, because a dash here would
    // read as "nothing needed".
    render: (d) =>
      d.app_equipment_score === null || d.app_equipment_score === undefined ? null : (
        <span className="tabular-nums">{d.app_equipment_score}</span>
      ),
    tooltip:
      'How many parts of this system we still need information about: the ' +
      'outdoor unit, the furnace or air handler, and the filter. The furnace ' +
      'or air handler counts when its record is not validated or its motor ' +
      'type is unknown. 0 means we have what we need. Sorts the systems ' +
      'needing the most to the top',
  },
  // FS-881: the open equipment reviews this Clip's edit drawer can act on,
  // projected from Firestore by the app-context resync. The operator filters
  // it to 1 or more (`f.app_open_review_count=1..`) to find the drawers with
  // something waiting, instead of querying Firestore by hand.
  {
    key: 'app_open_review_count',
    label: 'Open reviews',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    // Most waiting first — the reason to sort by this is to work the queue.
    defaultSortDir: 'desc',
    defaultWidth: 120,
    // 0 is a real answer and prints as 0. A row with no count (not synced
    // since the column was added, no app registration, or an API that
    // predates it) prints nothing: a dash or a 0 would read as "none waiting",
    // which is not what we know.
    render: (d) =>
      d.app_open_review_count === null || d.app_open_review_count === undefined ? null : (
        <span className="tabular-nums">{d.app_open_review_count}</span>
      ),
    tooltip:
      "Open equipment reviews this Clip's edit drawer can act on: catalog and photo reviews " +
      'waiting for Apply, held ones waiting for Dismiss, and any still being read or that could not be prepared',
  },
  // FS-752: whether the current filter's airflow record is calibrated. Kept
  // apart from Missing info and the score on purpose — a system can have
  // every fact on file and still be waiting for a calibrated day.
  {
    key: 'app_airflow_calibration',
    label: 'Airflow calibrated',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    // The search contract filters on it; it does not sort on it.
    sortable: false,
    filterable: true,
    facet: 'app_airflow_calibration',
    defaultSortDir: 'asc',
    defaultWidth: 160,
    render: (d) => (d.app_airflow_calibration ? airflowCalibrationLabel(d.app_airflow_calibration) : dash),
    csv: (d) => (d.app_airflow_calibration ? airflowCalibrationLabel(d.app_airflow_calibration) : null),
    optionLabel: (value) => airflowCalibrationLabel(value),
    tooltip:
      "Whether the current filter's airflow record is calibrated. Separate " +
      'from Missing info: it is not counted there or in the Equipment score',
  },
  {
    key: 'app_hvac_tonnage_source',
    label: 'Tonnage Source',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 130,
    render: (d) => (d.app_hvac_tonnage == null ? dash : sourceLabel(d.app_hvac_tonnage_source)),
    csv: (d) => (d.app_hvac_tonnage == null ? null : sourceLabel(d.app_hvac_tonnage_source)),
    optionLabel: (value) => sourceLabel(value),
    tooltip: 'Whether the tonnage was entered by a person, read from a photo, or decoded',
  },
  // FS-838: the unit the projection took the tonnage from, in the fixture
  // vocabulary. It replaces the old `Tonnage Component` column, keyed on the
  // side, which could not tell a furnace from an air handler. A saved layout
  // that showed the old key drops it once (unknown keys are not kept).
  {
    key: 'app_hvac_tonnage_component',
    label: 'Tonnage From',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'app_hvac_tonnage_component',
    defaultSortDir: 'asc',
    defaultWidth: 140,
    // The words sort as the codes do, so the server's order reads alphabetically.
    render: (d) => (d.app_hvac_tonnage_component ? componentName(d.app_hvac_tonnage_component) : dash),
    csv: (d) => (d.app_hvac_tonnage_component ? componentName(d.app_hvac_tonnage_component) : null),
    optionLabel: (value) => componentName(value),
    tooltip:
      'Which unit the system tonnage was taken from. "Indoor unit" means the indoor record ' +
      'does not say whether it is a furnace or an air handler.',
  },
  // FS-838: the "tonnage in conflict" filter is this column's Yes, sent to the
  // server as `f.app_hvac_tonnage_conflict=true`. A system with no tonnage
  // (NULL) matches neither Yes nor No.
  {
    key: 'app_hvac_tonnage_conflict',
    label: 'Tonnage In Conflict',
    group: 'App',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => yesNo(d.app_hvac_tonnage_conflict),
    tooltip:
      'Yes when another credible value (a person\'s entry, a manufacturer document or a label ' +
      'photo) disagrees with the system tonnage by more than 0.01 t, or when a ductless head ' +
      'sits on a larger outdoor unit. Filter on Yes to list them.',
  },
  {
    key: 'app_hvac_tonnage_alt',
    label: 'Disagreeing Tonnage',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    // Sorted on the number, the first thing the cell shows.
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 220,
    render: (d) => disagreeingTonnageText(d) ?? dash,
    csvHeader: 'Disagreeing Tonnage (t)',
    tooltip:
      'The value that disagrees with the system tonnage, and the record it is on. Empty when ' +
      'nothing disagrees.',
  },
  {
    key: 'app_hvac_blower_type',
    label: 'Motor Type',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 110,
    render: (d) => (d.app_hvac_blower_type ? motorTypeLabel(d.app_hvac_blower_type) : dash),
    csv: (d) => (d.app_hvac_blower_type ? motorTypeLabel(d.app_hvac_blower_type) : null),
    optionLabel: (value) => motorTypeLabel(value),
    tooltip:
      'Blower motor type from the nameplate; sparse — most labels and model ' +
      'numbers do not encode it. Records written before the vocabulary was ' +
      'settled are marked as legacy values',
  },
  {
    key: 'app_hvac_blower_type_source',
    label: 'Motor Type Source',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 150,
    // Just the source. What it is WORTH is the Motor Type Quality column,
    // which reads the server's code — the two were conflated here while the
    // ladder lived in the client.
    render: (d) => (d.app_hvac_blower_type ? sourceLabel(d.app_hvac_blower_type_source) : dash),
    csv: (d) => (d.app_hvac_blower_type ? sourceLabel(d.app_hvac_blower_type_source) : null),
    optionLabel: (value) => sourceLabel(value),
    tooltip: 'Whether the motor type was entered by a person, read from a photo, or decoded',
  },
  // Plan 119 Phase 3 (FS-627) — what the equipment model catalog resolved for
  // this system. Both opt-in, both sorted on the server so the ordering is
  // the data and not the page.
  {
    key: 'app_hvac_rated_esp_pa',
    label: 'Rated Static',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 220,
    // The manufacturer's own wording with the kind, worded by the same
    // formatter the device Overview uses. The row carries no document title,
    // so the cell names no document — the Overview does that.
    render: (d) => ratedStaticText(d),
    // The manufacturer's wording and the kind, as the cell prints them: the
    // value in Pa alone would drop which rating point it is.
    csv: (d) => ratedStaticText(d),
    tooltip:
      "The manufacturer's published external static pressure for this " +
      'system, from the model catalog. It is an operating point for the ' +
      'equipment, not a filter end-of-life threshold. Sorts on the value in Pa',
  },
  {
    key: 'app_hvac_catalog_model_id',
    label: 'Catalog Model',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 220,
    // The catalog row id is the sort key, never the thing shown: an operator
    // reads the manufacturer the catalog holds and the model it matched on.
    csv: (d) => {
      if (d.app_hvac_catalog_model_id == null) return 'not in our catalog';
      const words = [d.app_hvac_catalog_manufacturer?.trim(), d.app_hvac_model?.trim()]
        .filter(Boolean)
        .join(' ');
      return words || 'matched to the catalog';
    },
    render: (d) => {
      if (d.app_hvac_catalog_model_id == null) {
        return <span className="text-muted-foreground">not in our catalog</span>;
      }
      const manufacturer = d.app_hvac_catalog_manufacturer?.trim() ?? '';
      const model = d.app_hvac_model?.trim() ?? '';
      if (!manufacturer && !model) return 'matched to the catalog';
      return (
        <>
          {manufacturer}
          {manufacturer && model ? ' ' : ''}
          {/* The model number itself opens the catalog listing it matched. */}
          {model && (
            <ModelLink model={model} catalogModelId={d.app_hvac_catalog_model_id} />
          )}
        </>
      );
    },
    tooltip:
      'Whether the HVAC model number matched a model in the equipment ' +
      'catalog, and the manufacturer the catalog holds for it — which can ' +
      'differ from the brand on the record. Sorts matched models together',
  },
  // FS-752: who put the indoor model number on the record. Shown, never part
  // of whether the record counts — a homeowner's entry and a label reading
  // are judged by what the catalog makes of them, not by who typed them.
  {
    key: 'app_hvac_model_source',
    label: 'Indoor model entered by',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    // The search contract filters on it; it does not sort on it.
    sortable: false,
    filterable: true,
    facet: 'app_hvac_model_source',
    defaultSortDir: 'asc',
    defaultWidth: 160,
    render: (d) => modelSourceLabel(d.app_hvac_model_source),
    csv: (d) => modelSourceLabel(d.app_hvac_model_source),
    optionLabel: (value) => modelSourceLabel(value),
    tooltip:
      'Who put the furnace or air handler model number on the record: a ' +
      'person, a label photo or a decode. Not recorded for records written ' +
      'before the app kept this',
  },
  {
    key: 'app_ac_manufacturer',
    label: 'AC Brand',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 110,
    render: (d) => text(d.app_ac_manufacturer),
    tooltip:
      'AC/condenser manufacturer read from the nameplate photo or decoded ' +
      'from the model number',
  },
  {
    key: 'app_ac_model',
    label: 'AC Model',
    group: 'App',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 140,
    // Into the catalog, the same as the HVAC model number beside it: the
    // listing when the row matched one, the catalog search when it did not.
    render: (d) =>
      d.app_ac_model ? (
        <ModelLink model={d.app_ac_model} catalogModelId={d.app_ac_catalog_model_id} />
      ) : (
        dash
      ),
  },
  {
    key: 'app_ac_seer',
    label: 'SEER',
    group: 'App',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 85,
    render: (d) => num(d.app_ac_seer, 1),
    tooltip: 'Cooling efficiency (SEER2 where available, SEER otherwise)',
  },
  {
    key: 'app_filter_summary',
    label: 'Filter',
    group: 'Filter',
    type: 'text',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 170,
    render: (d) => text(d.app_filter_summary),
    tooltip: 'Filter summary from the MyFluxHive app registration',
  },
  {
    key: 'calibration_offset',
    label: 'Cal. Offset',
    group: 'Configuration',
    type: 'number',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 145,
    minWidth: 145,
    render: (d) => num(d.calibration_offset, 2),
  },
  {
    key: 'x46_fanside',
    label: 'Fanside',
    group: 'Configuration',
    type: 'boolean',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 120,
    minWidth: 120,
    render: (d) => yesNo(d.x46_fanside),
    tooltip: 'Whether the x46 sensor is mounted on the fan side (sets the ΔP sign)',
  },
  {
    key: 'entity_path',
    label: 'Entity Path',
    group: 'Identity',
    type: 'prefix',
    defaultVisible: true,
    sortable: true,
    filterable: true,
    facet: 'entity_path',
    defaultSortDir: 'asc',
    defaultWidth: 180,
    minWidth: 110,
    render: (d) => mono(d.entity_path),
    tooltip: 'Organizational responsibility path; a parent path covers its whole subtree',
  },
  {
    // Cell content is supplied by the page, not this registry: Source comes
    // from the live Firestore enrichment map, which row renders can't see.
    // DeviceList special-cases key 'source' in its body render. Registry
    // membership is what makes it hideable/reorderable like any column.
    key: 'source',
    label: 'Source',
    group: 'App',
    type: 'text',
    defaultVisible: true,
    sortable: false,
    filterable: false,
    defaultSortDir: 'asc',
    defaultWidth: 105,
    minWidth: 105,
    render: () => null,
    // Page-supplied too: DeviceList passes the App/Manual word to csvValueOf.
    csv: () => null,
    tooltip:
      'Where the device data comes from: App = live MyFluxHive app registration, Manual = set up by an operator only',
  },
  // --- Identity (opt-in) -----------------------------------------------------
  {
    key: 'device',
    label: 'Device',
    group: 'Identity',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    sortKey: 'name',
    filterable: false, // composite — filter the atomic Name/Description columns
    defaultSortDir: 'asc',
    defaultWidth: 260,
    minWidth: 160,
    // FS-825: labelled like the workspace header, with the filter name after
    // the owner's; the row has no Clip count, so an unnamed Clip reads as
    // before. The sort stays on the serial, shown on the second line.
    csv: (d) => `${searchRowClipLabel(d)} (${d.name}, ${d.id})`,
    render: (d) => (
      <>
        <div className="font-medium flex items-center gap-2">
          <span className={d.decommissioned ? 'line-through text-muted-foreground' : ''}>
            {searchRowClipLabel(d)}
          </span>
          <DecommissionedBadge decommissioned={d.decommissioned} />
        </div>
        <div className="text-muted-foreground font-mono text-xs break-all">
          {d.name} ({d.id})
        </div>
      </>
    ),
  },
  {
    key: 'description',
    label: 'Description',
    group: 'Identity',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 180,
    render: (d) => text(d.description),
  },
  {
    key: 'thing_name',
    label: 'Thing Name',
    group: 'Identity',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 230,
    render: (d) => mono(d.thing_name),
  },
  // --- Lifecycle ---------------------------------------------------------------
  // FS-777 (Plan 130): each stage of a Clip's life as a date and a yes/no twin,
  // in the order the stages happen. Keys of the columns that already existed
  // are unchanged, so saved views and links keep working; only their labels
  // moved to sentence case. Created has no twin: it is always yes.
  {
    key: 'created_date',
    label: 'Created',
    group: 'Lifecycle',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => date(d.created_date),
    tooltip: "When this Clip's device record was created",
  },
  {
    key: 'lifecycle_reset_at',
    label: 'Returned to inventory',
    group: 'Lifecycle',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) => date(d.lifecycle_reset_at),
    tooltip: 'When this Clip was last returned to inventory; blank if it never was',
  },
  {
    key: 'is_in_inventory',
    label: 'Is in inventory',
    group: 'Lifecycle',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => yesNo(d.is_in_inventory),
    tooltip: 'Yes when this Clip was returned to inventory and has not been activated since',
  },
  {
    key: 'activated_at',
    label: 'Activated',
    group: 'Lifecycle',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => date(d.activated_at),
    tooltip: 'Device first came online; not affected by filter changes',
  },
  {
    key: 'is_activated',
    label: 'Is activated',
    group: 'Lifecycle',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 100,
    render: (d) => yesNo(d.is_activated),
    tooltip: 'Yes when this Clip has an Activated date; returning it to inventory clears it',
  },
  {
    key: 'registered_at',
    label: 'Registered',
    group: 'Lifecycle',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => date(d.registered_at),
    tooltip: 'When the app record this Clip is matched to was created',
  },
  {
    key: 'is_registered',
    label: 'Is registered',
    group: 'Lifecycle',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => yesNo(d.is_registered),
    tooltip: 'Yes when at least one app record names this Clip',
  },
  {
    key: 'first_reading_at',
    label: 'First reading',
    group: 'Lifecycle',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) => date(d.first_reading_at),
    tooltip: 'When this Clip first stored a reading',
  },
  {
    key: 'has_first_reading',
    label: 'Has first reading',
    group: 'Lifecycle',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 130,
    render: (d) => yesNo(d.has_first_reading),
    tooltip:
      'Yes when this Clip has a First reading date or any stored data from this setup; ' +
      'returning it to inventory clears it',
  },
  {
    key: 'first_filter_installed',
    label: 'First filter installed',
    group: 'Lifecycle',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 130,
    render: (d) => date(d.first_filter_installed),
    tooltip: 'Install date of the earliest filter on record for this Clip; corrected entries replace the ones they fix',
  },
  {
    key: 'is_installed',
    label: 'Is installed',
    group: 'Lifecycle',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 100,
    render: (d) => yesNo(d.is_installed),
    tooltip: 'Yes when a filter install is on record for this Clip',
  },
  {
    key: 'install_date',
    label: 'Current filter installed',
    group: 'Lifecycle',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 130,
    render: (d) => date(d.install_date),
    tooltip: 'When the filter now in the unit was installed; it changes with every filter change',
  },
  {
    key: 'decommissioned',
    label: 'Is decommissioned',
    group: 'Lifecycle',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 130,
    render: (d) => yesNo(d.decommissioned),
    tooltip: "Yes when this Clip is marked decommissioned in its settings",
  },
  // --- Location (opt-in) -------------------------------------------------------
  {
    key: 'city',
    label: 'City',
    group: 'Location',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 120,
    render: (d) => text(d.city),
  },
  {
    key: 'state',
    label: 'State',
    group: 'Location',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'state',
    defaultSortDir: 'asc',
    defaultWidth: 80,
    render: (d) => text(d.state),
  },
  {
    key: 'country',
    label: 'Country',
    group: 'Location',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'country',
    defaultSortDir: 'asc',
    defaultWidth: 90,
    render: (d) => text(d.country),
  },
  {
    key: 'time_zone',
    label: 'Time Zone',
    group: 'Location',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'time_zone',
    defaultSortDir: 'asc',
    defaultWidth: 160,
    render: (d) => text(d.time_zone),
  },
  {
    key: 'latitude',
    label: 'Latitude',
    group: 'Location',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 100,
    render: (d) => num(d.latitude, 5),
  },
  {
    key: 'longitude',
    label: 'Longitude',
    group: 'Location',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 100,
    render: (d) => num(d.longitude, 5),
  },
  // --- Configuration (opt-in) --------------------------------------------------
  {
    key: 'firmware_version',
    label: 'Firmware',
    group: 'Configuration',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'firmware_version',
    defaultSortDir: 'desc',
    defaultWidth: 100,
    render: (d) => text(d.firmware_version),
    tooltip: 'Reported by the device at boot / device-info publish',
  },
  {
    key: 'bootloader_version',
    label: 'Bootloader',
    group: 'Configuration',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'bootloader_version',
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => text(d.bootloader_version),
  },
  {
    // FS-948: WIFI_VERSION from DeviceInfo, free text as reported; changes
    // independently of the Clip firmware.
    key: 'wifi_version',
    label: 'WiFi Module',
    group: 'Configuration',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'wifi_version',
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) => text(d.wifi_version),
    tooltip: "Firmware of the Clip's WiFi module, reported with the Clip firmware (0.19 and later)",
  },
  {
    // FS-637: set by hand in the Edit modal from the twelve V-1 Production
    // variants (utils/clipAssy.ts); the facet carries whichever are in use.
    key: 'assy',
    label: 'Assy',
    group: 'Configuration',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'assy',
    defaultSortDir: 'asc',
    defaultWidth: 90,
    render: (d) => text(d.assy),
    tooltip: 'Clip assembly variant from the V-1 Production drawing (MF75 … MF600A)',
  },
  // --- Filter (opt-in) ----------------------------------------------------------
  {
    key: 'filter_change_date',
    label: 'Filter Changed',
    group: 'Filter',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 125,
    render: (d) => date(d.filter_change_date),
  },
  {
    key: 'filter_start_date',
    label: 'Filter Started',
    group: 'Filter',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) => date(d.filter_start_date),
  },
  {
    key: 'filter_start_dp',
    label: 'Start ΔP (Pa)',
    group: 'Filter',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 95,
    render: (d) => num(d.filter_start_dp, 1),
  },
  // FS-580: the loading rate, so the fleet can be sorted by how fast filters
  // are actually loading. The server re-derives the app card's number, so the
  // two agree for the same device on the same day.
  {
    key: 'daily_change_pa',
    label: 'ΔP/Day (30d, Pa)',
    group: 'Filter',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) => num(d.daily_change_pa, 3),
    tooltip:
      'Daily Change (last 30d trend): how fast the current filter is loading, in Pa per day — the same number as the App Graphs card. Fitted over the last 30 days with a high-speed reading, starting the day after the current filter period began. Blank when the card shows none: too few days yet, no filter-life baseline, or nothing processed in 30 days.',
  },
  // FS-443: per-device pressure thresholds. A dash means the device is on the
  // fleet default (125 Pa end of life) and the legacy percentage-of-life
  // notification, so an empty column reads as "never configured".
  {
    key: 'filter_eol_pa',
    label: 'End of Life (Pa)',
    group: 'Filter',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    tooltip: 'Pressure at which filter life reads 0%. Blank = fleet default (125 Pa).',
    render: (d) => num(d.filter_eol_pa, 1),
  },
  {
    key: 'filter_notify_pa',
    label: 'Notify At (Pa)',
    group: 'Filter',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 115,
    tooltip:
      'Pressure at which the replacement alert fires. Blank = the filter-life percentage trigger.',
    render: (d) => num(d.filter_notify_pa, 1),
  },
  {
    key: 'filter_thickness_inches',
    label: 'Thickness (in)',
    group: 'Filter',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 110,
    render: (d) => num(d.filter_thickness_inches, 1),
  },
  {
    key: 'filter_is_new',
    label: 'Filter Is New',
    group: 'Filter',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 110,
    render: (d) => yesNo(d.filter_is_new),
  },
  {
    key: 'filter_baseline_established',
    label: 'Baseline Est.',
    group: 'Filter',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 115,
    render: (d) => yesNo(d.filter_baseline_established),
  },
  {
    key: 'filter_baseline_source',
    label: 'Baseline Source',
    group: 'Filter',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'filter_baseline_source',
    defaultSortDir: 'asc',
    defaultWidth: 130,
    render: (d) => text(d.filter_baseline_source),
  },
  {
    key: 'filter_catalog_id',
    label: 'Catalog Filter',
    group: 'Filter',
    type: 'enum',
    intValues: true,
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'filter_catalog',
    defaultSortDir: 'asc',
    defaultWidth: 200,
    render: (d) => text(d.filter_catalog_label),
    csv: (d) => d.filter_catalog_label,
  },
  // --- Plan 119 Phase 4 ----------------------------------------------------
  // Who said what the filter is, and which listing its curve came from. Both
  // opt-in: they answer a question an operator asks on purpose ("which filters
  // did somebody just type in", "which curves came off a listing we have since
  // edited") rather than one the default view has to carry.
  {
    key: 'app_filter_manufacturer_source',
    label: 'Filter Identity Source',
    group: 'Filter',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'app_filter_manufacturer_source',
    defaultSortDir: 'asc',
    defaultWidth: 170,
    // The brand's source stands for the identity: it is the field every filter
    // record has, and it is the one the picker writes. The full rule across
    // all four fields is the server's Filter Quality column.
    render: (d) =>
      d.app_filter_summary ? filterIdentitySourceLabel(d.app_filter_manufacturer_source) : dash,
    csv: (d) =>
      d.app_filter_summary ? filterIdentitySourceLabel(d.app_filter_manufacturer_source) : null,
    optionLabel: (value) => filterIdentitySourceLabel(value),
    tooltip:
      'Whether the filter brand was read from a label photo or entered by a '
      + 'person. Records written before the app kept track say so',
  },
  {
    key: 'filter_catalog_match_source',
    label: 'Catalog Source',
    group: 'Filter',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'filter_catalog_match_source',
    defaultSortDir: 'asc',
    defaultWidth: 150,
    render: (d) => text(d.filter_catalog_match_source),
    tooltip:
      'The catalog listing the nightly match chose, as it read AT MATCH TIME. '
      + 'A listing edited since then still shows what the match used, which is '
      + 'what this device\'s curve was graded on',
  },
  // --- App (opt-in) --------------------------------------------------------------
  {
    key: 'owner',
    label: 'Owner + Location',
    group: 'App',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    sortKey: 'app_owner_name',
    filterable: false, // composite — filter User / Owner Email / Location
    defaultSortDir: 'asc',
    defaultWidth: 230,
    minWidth: 140,
    csv: (d) => {
      if (!d.context_status || d.context_status === 'missing') return 'Not registered';
      const who = d.app_owner_name || d.app_owner_email || 'Unknown';
      const where = d.app_location_name ? ` · ${d.app_location_name}` : '';
      const dup = d.context_status === 'ambiguous' ? ' (duplicate app registrations)' : '';
      return `${who}${where}${dup}`;
    },
    render: (d) =>
      !d.context_status || d.context_status === 'missing' ? (
        <span className="text-muted-foreground">Not registered</span>
      ) : (
        <span>
          {d.app_owner_name || d.app_owner_email || 'Unknown'}
          {d.app_location_name && <span className="text-muted-foreground"> · {d.app_location_name}</span>}
          {d.context_status === 'ambiguous' && (
            <span className="ml-1 text-status-warning-foreground" title="Duplicate app registrations">
              ⚠
            </span>
          )}
        </span>
      ),
  },
  {
    key: 'context_status',
    label: 'Registration',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    facet: 'context_status',
    defaultSortDir: 'asc',
    defaultWidth: 110,
    render: (d) => text(d.context_status),
  },
  {
    key: 'app_owner_email',
    label: 'Owner Email',
    group: 'App',
    type: 'text',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 200,
    render: (d) => text(d.app_owner_email),
  },
  // FS-646: Yes is exactly what the send gate's verified tier counts as a
  // confirmed address. A dash is no claim holder or no address. FS-1021: it is
  // evidence about the ADDRESS only. Whether the owner gets HVAC system emails
  // is their own setting, which this column does not read.
  {
    key: 'owner_email_verified',
    label: 'Email verified',
    group: 'App',
    type: 'boolean',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) => yesNo(d.owner_email_verified),
    tooltip:
      "Whether the owner's email address is confirmed: by the owner clicking our verification " +
      'link, their Google or Apple sign-in, mail we delivered before, or an internal FluxHive ' +
      'address. It describes the address only, not whether the owner gets HVAC system emails.',
  },
  {
    key: 'app_location_name',
    label: 'Location',
    group: 'App',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    filterKey: 'app_location_id',
    facet: 'app_location',
    defaultSortDir: 'asc',
    defaultWidth: 140,
    render: (d) => text(d.app_location_name),
  },
  // --- Operations (opt-in, Plan 103 — Randy's fleet-review fields) -----------------
  {
    key: 'runtime_since_filter_h',
    label: 'Operating Hours (filter)',
    group: 'Operations',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 145,
    render: (d) => num(d.runtime_since_filter_h),
    tooltip:
      'Estimated fan-on hours on the current filter (since the last filter change; whole device history if it was never changed). Clustering-derived from the daily analysis, ~2 days behind live data.',
  },
  {
    key: 'runtime_per_day_30d_h',
    label: 'Runtime/Day (30d, h)',
    group: 'Operations',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 140,
    render: (d) => num(d.runtime_per_day_30d_h, 1),
    tooltip:
      'Average estimated fan-on hours per day over the last 30 days. Days without analysis data count as zero, so an offline device cannot show an inflated figure.',
  },
  {
    key: 'return_static_max_pa',
    label: 'Return Static (30d high)',
    csvHeader: 'Return Static (30d high, Pa)',
    group: 'Operations',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 165,
    minWidth: 110,
    // Deliberately unbanded: this column reports a measurement and takes no
    // position on whether it is good or bad. A threshold table exists but was
    // given for duct-system static, and the Clip measures at the filter, so
    // tinting cells would assert a mapping nobody has confirmed. Sorting
    // descending already answers "worst case at a glance", which is the ask.
    render: (d) =>
      d.return_static_max_pa === null ? (
        dash
      ) : (
        <span
          className="whitespace-nowrap tabular-nums"
          title={`Highest of ${d.return_static_days} day${
            d.return_static_days === 1 ? '' : 's'
          } with a value`}
        >
          {Math.round(d.return_static_max_pa)} Pa
        </span>
      ),
    tooltip:
      'Highest daily return-side pressure swing in the window: the last 30 days, or since the current filter went in, whichever is shorter. ' +
      'Clustering-derived from the nightly analysis, ~2 days behind live data; blank means no day in the window had a usable fan on/off transition.',
  },
  {
    key: 'open_alerts',
    label: 'Open Alerts',
    group: 'Operations',
    type: 'enum',
    defaultVisible: false,
    sortable: true,
    sortKey: 'open_alert_count',
    filterable: true,
    filterKey: 'open_alert_types',
    facet: 'open_alert_types',
    optionLabel: alertLabel,
    defaultSortDir: 'desc',
    defaultWidth: 190,
    minWidth: 110,
    render: (d) => alertChips(d.open_alerts),
    csv: (d) => (d.open_alerts ?? []).map((a) => alertLabel(a.type)).join('; ') || null,
    tooltip:
      'Alerts currently open on the device (color = severity). A dashed outline marks internal-only alerts the app user never sees. Sorts by open-alert count.',
  },
  // --- Telemetry (opt-in) ----------------------------------------------------------
  {
    key: 'days_since_heard',
    label: 'Days Since Heard',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 135,
    render: (d) =>
      d.days_since_heard === null ? (
        <span className="text-muted-foreground">never</span>
      ) : d.days_since_heard < 1 ? (
        <span className="text-green-700">&lt; 1</span>
      ) : (
        Math.floor(d.days_since_heard)
      ),
    tooltip: 'Days since the last reading arrived (any reading, not just the 7-day window)',
  },
  {
    key: 'last_heard',
    label: 'Last Heard',
    // The file carries the exact time, not the "21 min ago" the cell prints.
    csvHeader: 'Last Heard (UTC)',
    group: 'Telemetry',
    type: 'date',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 150,
    render: (d) =>
      d.last_heard ? <span className="whitespace-nowrap">{relTime(d.last_heard)}</span> : dash,
    tooltip: 'How long ago the most recent reading arrived; sorts by the exact (UTC) time',
  },
  {
    key: 'battery_pct',
    label: 'Battery %',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 95,
    render: (d) => (d.battery_pct === null ? dash : `${Math.round(d.battery_pct)}%`),
    tooltip: 'From the latest read bundle (2.0 V → 0 %, 3.0 V → 100 %)',
  },
  {
    key: 'airflow_percentage',
    label: 'Filter Life',
    csvHeader: 'Filter Life (%)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'asc',
    defaultWidth: 95,
    render: (d) => (d.airflow_percentage === null ? dash : `${Math.round(d.airflow_percentage)}%`),
    tooltip: 'Updated by the nightly analysis run',
  },
  {
    key: 'pressure_x46_min_7d',
    label: 'x46 Min (7d, Pa)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 135,
    render: (d) => num(d.pressure_x46_min_7d),
  },
  {
    key: 'pressure_x46_max_7d',
    label: 'x46 Max (7d, Pa)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 135,
    render: (d) => num(d.pressure_x46_max_7d),
  },
  {
    key: 'pressure_x47_min_7d',
    label: 'x47 Min (7d, Pa)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 135,
    render: (d) => num(d.pressure_x47_min_7d),
  },
  {
    key: 'pressure_x47_max_7d',
    label: 'x47 Max (7d, Pa)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 135,
    render: (d) => num(d.pressure_x47_max_7d),
  },
  {
    key: 'dp_current',
    label: 'ΔP Current (Pa)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 120,
    render: (d) =>
      d.dp_current === null || d.dp_current === undefined ? (
        dash
      ) : (
        <span
          className={
            d.days_since_heard !== null && d.days_since_heard > 1 ? 'text-muted-foreground' : ''
          }
        >
          {d.dp_current.toFixed(1)}
        </span>
      ),
    tooltip:
      'Calibrated differential pressure of the latest reading, in Pa (device-debug formula). Grayed when the last reading is over a day old — the value is only as fresh as Last Heard.',
  },
  {
    key: 'dp_min_7d',
    label: 'ΔP Min (7d, Pa)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 130,
    render: (d) => num(d.dp_min_7d, 1),
    tooltip: 'Calibrated differential pressure in Pa (same formula as the device debug view)',
  },
  {
    key: 'dp_max_7d',
    label: 'ΔP Max (7d, Pa)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 130,
    render: (d) => num(d.dp_max_7d, 1),
    tooltip: 'Calibrated differential pressure in Pa (same formula as the device debug view)',
  },
  {
    key: 'readings_7d',
    label: 'Readings (7d)',
    group: 'Telemetry',
    type: 'number',
    defaultVisible: false,
    sortable: true,
    filterable: true,
    defaultSortDir: 'desc',
    defaultWidth: 115,
    render: (d) => (d.readings_7d === null || d.readings_7d === undefined ? dash : d.readings_7d),
    tooltip: 'Reading count over the trailing 7 days — a data-flow health signal',
  },
  // --- Tags (FS-501) ---------------------------------------------------------
  {
    key: 'tags',
    label: 'Tags',
    group: 'Identity',
    type: 'enum',
    intValues: true,
    // Opt-in like every other non-legacy column; the operator turns it on from
    // the column chooser once they have tags worth seeing.
    defaultVisible: false,
    // Multi-valued, so there is no single value to order rows by.
    sortable: false,
    filterable: true,
    facet: 'tags',
    defaultSortDir: 'asc',
    defaultWidth: 180,
    minWidth: 90,
    render: (d) => tagChips(d.tags),
    tooltip:
      'Labels attached to the device. Filter by one or more tags, or use the Not Tagged filter to hide a group such as development units.',
  },
  {
    key: 'not_tags',
    label: 'Not Tagged',
    group: 'Identity',
    type: 'enum',
    intValues: true,
    // Filter-only: it shares the Tags facet and exists so a saved default view
    // can hide development units without hand-listing every other tag.
    filterOnly: true,
    defaultVisible: false,
    sortable: false,
    filterable: true,
    facet: 'tags',
    defaultSortDir: 'asc',
    defaultWidth: 180,
    render: () => null,
    tooltip: 'Hide devices carrying any of the selected tags.',
  },
];

/** Columns the table can render (everything except filter-only entries). */
export const DISPLAY_COLUMNS: ColumnDef[] = COLUMN_REGISTRY.filter((c) => !c.filterOnly);

export const COLUMN_BY_KEY: Record<string, ColumnDef> = Object.fromEntries(
  COLUMN_REGISTRY.map((c) => [c.key, c]),
);

/** Display columns by key — the validation set for saved column prefs. */
export const DISPLAY_BY_KEY: Record<string, ColumnDef> = Object.fromEntries(
  DISPLAY_COLUMNS.map((c) => [c.key, c]),
);

export const DEFAULT_MIN_WIDTH = 60;

/** FS-745 — the column's CSV header: its label unless it declares one. */
export const csvHeaderOf = (c: ColumnDef): string => c.csvHeader ?? c.label;

/**
 * FS-745 — the column's cell for one row, as a plain CSV value: the column's
 * own `csv` when it declares one, otherwise the row field named by its key.
 */
export const csvValueOf = (c: ColumnDef, row: DeviceSearchRow): CsvValue => {
  if (c.csv) return c.csv(row);
  const v = (row as unknown as Record<string, unknown>)[c.key];
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' || typeof v === 'number') return v;
  if (Array.isArray(v)) return v.length > 0 ? v.join('; ') : null;
  return null;
};

/** The sort key a column's header click drives (composites declare one). */
export const sortKeyOf = (c: ColumnDef): string => c.sortKey ?? c.key;
/** The f.<key> filter param a column's filter editor drives. */
export const filterKeyOf = (c: ColumnDef): string => c.filterKey ?? c.key;

/** All keys the client will accept in a ?sort= param. */
export const VALID_SORT_KEYS = new Set(
  COLUMN_REGISTRY.filter((c) => c.sortable).map((c) => sortKeyOf(c)),
);

/** filter key → its column def (filter editors + validation). */
export const FILTERABLE_BY_FILTER_KEY: Record<string, ColumnDef> = Object.fromEntries(
  COLUMN_REGISTRY.filter((c) => c.filterable).map((c) => [filterKeyOf(c), c]),
);

export const GROUP_ORDER: ColumnGroup[] = [
  'Identity',
  'Lifecycle',
  'Location',
  'Configuration',
  'Filter',
  'App',
  'Operations',
  'Telemetry',
];
